import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { fieldDir } from '../i18n/field-dir';
import { t } from '../services/language-manager';
import {
  INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH,
  INFO_FIELD_LABEL_MAX_LENGTH,
  INFO_FIELD_MATCHES_MAX,
  INFO_FIELDS_MAX,
  INFO_FIELDS_MAX_IMPORTANT,
  INFO_LINK_URL_MAX_LENGTH,
  INFO_MATCH_EQUALS_MAX_LENGTH,
  INFO_MATCH_NUMBER_MAX_LENGTH,
  INFO_MATCH_TEXT_MAX_LENGTH,
  type InfoFieldChannels,
  type InfoFieldDisplay,
  type InfoMatchOp,
} from '../services/wizard-backend';
import {
  INFO_MATCH_OPS,
  NOTIFICATION_ROWS,
  countImportantFields,
  countNotifyFields,
  emptyInfoField,
  emptyInfoMatch,
  isCustomerSetAttribute,
  isNumberOp,
  linkAttributeKey,
  unreachableConditions,
  validateInfoFields,
  wholeAddressAttribute,
  type EditableInfoField,
  type InfoFieldInput,
  type InfoMatchInput,
} from '../services/actions-info-fields';
import { confirmDialog } from './ConfirmDialog';
import { discardButtonProps } from './CustomFieldsEditor';

/** A 400 INFO_FIELDS_INVALID from the server, resolved to this action. */
export interface InfoFieldsServerError {
  /** Index into the action's infoFields array; absent = list-level problem. */
  fieldIndex?: number;
  message: string;
}

interface InfoFieldsEditorProps {
  /** Parent action key; namespaces the input ids. */
  actionKey: string;
  fields: EditableInfoField[];
  onChange: (fields: EditableInfoField[]) => void;
  serverError: InfoFieldsServerError | null;
  /** True after a failed save: show every problem, not only touched inputs. */
  showAllErrors: boolean;
}

const DISPLAYS: InfoFieldDisplay[] = ['value', 'match', 'link', 'externalTag'];
const DISPLAY_LABEL_KEYS: Record<InfoFieldDisplay, string> = {
  value: 'settings-actions-info-display-value',
  match: 'settings-actions-info-display-match',
  link: 'settings-actions-info-display-link',
  externalTag: 'settings-actions-info-display-external-tag',
};
const DISPLAY_HELP_KEYS: Record<InfoFieldDisplay, string> = {
  value: 'settings-actions-info-display-value-help',
  match: 'settings-actions-info-display-match-help',
  link: 'settings-actions-info-display-link-help',
  externalTag: 'settings-actions-info-display-external-tag-help',
};
const OP_LABEL_KEYS: Record<InfoMatchOp, string> = {
  equals: 'settings-actions-info-op-equals',
  not_equals: 'settings-actions-info-op-not-equals',
  contains: 'settings-actions-info-op-contains',
  starts_with: 'settings-actions-info-op-starts-with',
  any: 'settings-actions-info-op-any',
  gt: 'settings-actions-info-op-gt',
  gte: 'settings-actions-info-op-gte',
  lt: 'settings-actions-info-op-lt',
  lte: 'settings-actions-info-op-lte',
};
const OP_HINT_KEYS: Record<InfoMatchOp, string> = {
  equals: 'settings-actions-info-op-equals-hint',
  not_equals: 'settings-actions-info-op-not-equals-hint',
  contains: 'settings-actions-info-op-contains-hint',
  starts_with: 'settings-actions-info-op-starts-with-hint',
  any: 'settings-actions-info-op-any-hint',
  gt: 'settings-actions-info-op-gt-hint',
  gte: 'settings-actions-info-op-gte-hint',
  lt: 'settings-actions-info-op-lt-hint',
  lte: 'settings-actions-info-op-lte-hint',
};
const CHANNELS: InfoFieldChannels[] = ['all', 'voice', 'digital'];
const CHANNEL_LABEL_KEYS: Record<InfoFieldChannels, string> = {
  all: 'settings-actions-info-channels-all',
  voice: 'settings-actions-info-channels-voice',
  digital: 'settings-actions-info-channels-digital',
};

// Same selector as the custom fields editor: leaving an input for a discard control must not mark it touched.
const DISCARD_FOCUS_SELECTOR = '[data-discard], [role="alertdialog"]';

type FocusTo = 'summary' | 'handle' | 'up' | 'down' | 'invalid' | 'label' | 'add';

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}

/** Anything typed or chosen on a new field, so Cancel asks before discarding it. */
function hasFieldInput(field: EditableInfoField): boolean {
  return Boolean(
    field.label.trim() || field.attributeKey.trim() || field.display !== 'value' || field.notify || field.important
    || field.channels !== 'all' || field.linkUrl.trim()
    || field.matches.some((m) => m.op !== 'equals' || m.equals.trim() || m.text.trim()),
  );
}

/** Any client problem on the row or its conditions (shown on the summary, touched or not). */
function rowHasProblem(validation: ReturnType<typeof validateInfoFields>, index: number): boolean {
  const row = validation.rows[index];
  if (row && Object.values(row).some(Boolean)) return true;
  return (validation.matchRows[index] ?? []).some((m) => m && Object.values(m).some(Boolean));
}

/** Where a dragged row lands: before or after the row under the pointer. */
function insertionIndex(from: number, over: number, after: boolean): number {
  const target = after ? over + 1 : over;
  return from < target ? target - 1 : target;
}

/**
 * The fields block of a Conversation info action: an ordered list of
 * participant-data rows with reorder / remove, live caps counter, per-input
 * validation, and placement of a server-side INFO_FIELDS_INVALID on the row
 * it points at. Each field is a collapsed summary row; one row's editor is
 * open at a time. Add field opens a new field whose only controls are Save
 * field and Cancel (the action is still saved by its own button).
 * Reorder: drag the handle, use Move up / Move down, or press Alt+Up / Alt+Down
 * on a row's controls; each move is announced.
 */
export function InfoFieldsEditor({ actionKey, fields, onChange, serverError, showAllErrors }: InfoFieldsEditorProps) {
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  // Rows present at a failed save keep showing every problem while the admin fixes them.
  const [submittedRows, setSubmittedRows] = useState<Set<string>>(() => new Set());
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [focusTarget, setFocusTarget] = useState<{ rowKey?: string; to: FocusTo } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [drag, setDrag] = useState<{ from: string; over: string | null; after: boolean } | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const pendingIndex = fields.findIndex((f) => f.unsaved);
  const pending = pendingIndex >= 0 ? fields[pendingIndex] : null;
  const rowId = (rowKey: string, part: string) => `action-${actionKey}-info-${rowKey}-${part}`;

  useEffect(() => {
    if (!showAllErrors && !serverError) return;
    setSubmittedRows((prev) => {
      const next = new Set(prev);
      for (const field of fields) {
        next.add(field.rowKey);
        for (const match of field.matches) next.add(match.rowKey);
      }
      return next;
    });
    const failed = validateInfoFields(fields);
    const rowProblem = fields.findIndex((_, i) => rowHasProblem(failed, i));
    const target = pendingIndex >= 0
      ? pendingIndex
      : serverError?.fieldIndex !== undefined
        ? serverError.fieldIndex
        : rowProblem;
    if (target >= 0 && fields[target]) setOpenRow(fields[target].rowKey);
    // Snapshot the rows at the moment a save fails, not on every edit.
  }, [showAllErrors, serverError]);

  useEffect(() => {
    if (!focusTarget) return;
    setFocusTarget(null);
    if (focusTarget.to === 'add') {
      addRef.current?.focus();
      return;
    }
    const rowKey = focusTarget.rowKey ?? '';
    if (focusTarget.to === 'invalid') {
      document.getElementById(rowId(rowKey, 'body'))?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }
    const el = document.getElementById(rowId(rowKey, focusTarget.to)) as HTMLButtonElement | null;
    // A move button that just became disabled (first or last place) hands the focus to the row's summary.
    if (el && !el.disabled) el.focus();
    else document.getElementById(rowId(rowKey, 'summary'))?.focus();
  }, [focusTarget, actionKey]);

  const validation = validateInfoFields(fields);
  const notifyCount = countNotifyFields(fields);
  const importantCount = countImportantFields(fields);
  const atTotalCap = fields.length >= INFO_FIELDS_MAX;

  const update = (index: number, patch: Partial<EditableInfoField>) => {
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const moveTo = (from: number, to: number, focusTo: FocusTo) => {
    if (to < 0 || to >= fields.length || to === from) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    setAnnouncement(t('settings-actions-info-moved', {
      position: String(to + 1),
      total: String(fields.length),
    }));
    setFocusTarget({ rowKey: moved.rowKey, to: focusTo });
  };

  const remove = (index: number) => {
    if (fields[index].rowKey === openRow) setOpenRow(null);
    onChange(fields.filter((_, i) => i !== index));
    setFocusTarget({ to: 'add' });
  };

  const add = () => {
    if (atTotalCap || pending) return;
    const next = { ...emptyInfoField(), unsaved: true };
    onChange([...fields, next]);
    setOpenRow(next.rowKey);
    setFocusTarget({ rowKey: next.rowKey, to: 'label' });
  };

  /** Save field: this row's checks only; on success it becomes a saved, collapsed row. */
  const saveField = (index: number) => {
    const field = fields[index];
    if (rowHasProblem(validateInfoFields(fields), index)) {
      setSubmittedRows((prev) => {
        const next = new Set(prev).add(field.rowKey);
        for (const match of field.matches) next.add(match.rowKey);
        return next;
      });
      setFocusTarget({ rowKey: field.rowKey, to: 'invalid' });
      return;
    }
    onChange(fields.map((f, i) => (i === index ? { ...f, unsaved: undefined } : f)));
    setOpenRow(null);
    setFocusTarget({ rowKey: field.rowKey, to: 'summary' });
  };

  /** Cancel on a new field: drop it, asking first if anything was entered. */
  const cancelField = async (index: number) => {
    const field = fields[index];
    if (hasFieldInput(field)) {
      const discard = await confirmDialog({
        title: t('settings-actions-fields-discard-title'),
        subject: field.label.trim() || undefined,
        message: t('settings-actions-fields-discard-message'),
        confirmLabel: t('settings-actions-discard'),
        cancelLabel: t('settings-actions-discard-keep'),
        danger: true,
      });
      if (!discard) return;
    }
    setOpenRow(null);
    onChange(fields.filter((f) => f.rowKey !== field.rowKey));
    setFocusTarget({ to: 'add' });
  };

  const touchId = (rowKey: string, input: string) => `${rowKey}:${input}`;
  const markTouched = (rowKey: string, input: string, nextFocus: EventTarget | null) => {
    if ((nextFocus as Element | null)?.closest?.(DISCARD_FOCUS_SELECTOR)) return;
    const id = touchId(rowKey, input);
    if (touched.has(id)) return;
    setTouched((prev) => new Set(prev).add(id));
  };

  const inputError = (index: number, input: InfoFieldInput, rawValue: string, alwaysShow = false): string | null => {
    const key = validation.rows[index]?.[input];
    if (!key) return null;
    const rowKey = fields[index].rowKey;
    const show = alwaysShow || submittedRows.has(rowKey) || rawValue !== '' || touched.has(touchId(rowKey, input));
    return show ? t(key) : null;
  };
  const matchError = (fieldIndex: number, matchIndex: number, input: InfoMatchInput, rawValue: string): string | null => {
    const key = validation.matchRows[fieldIndex]?.[matchIndex]?.[input];
    if (!key) return null;
    const matchRowKey = fields[fieldIndex].matches[matchIndex].rowKey;
    const show = submittedRows.has(matchRowKey) || rawValue !== '' || touched.has(touchId(matchRowKey, input));
    return show ? t(key) : null;
  };
  const rowServerError = (index: number): string | null => (
    serverError && serverError.fieldIndex === index ? serverError.message : null
  );
  const blockError = serverError && serverError.fieldIndex === undefined
    ? serverError.message
    : validation.block ? t(validation.block) : null;

  /** Alt+Up / Alt+Down on a row's summary, handle or move buttons; the focus stays on the same control. */
  const handleRowKeyDown = (e: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || pending) return;
    const focusTo = (e.target as HTMLElement).dataset.infoFocus as FocusTo | undefined;
    if (!focusTo) return;
    e.preventDefault();
    moveTo(index, index + (e.key === 'ArrowUp' ? -1 : 1), focusTo);
  };

  const onDragStart = (e: DragEvent<HTMLButtonElement>, rowKey: string) => {
    e.dataTransfer.effectAllowed = 'move';
    // Firefox starts a drag only with data set.
    e.dataTransfer.setData('text/plain', '');
    const row = document.getElementById(rowId(rowKey, 'row'));
    if (row) e.dataTransfer.setDragImage(row, 24, 18);
    setDrag({ from: rowKey, over: null, after: false });
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>, rowKey: string) => {
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    if (drag.over !== rowKey || drag.after !== after) setDrag({ ...drag, over: rowKey, after });
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!drag) return;
    e.preventDefault();
    const from = fields.findIndex((f) => f.rowKey === drag.from);
    const over = fields.findIndex((f) => f.rowKey === drag.over);
    setDrag(null);
    if (from < 0 || over < 0) return;
    moveTo(from, insertionIndex(from, over, drag.after), 'handle');
  };

  const counterText = t('settings-actions-info-counter', {
    total: String(fields.length),
    maxTotal: String(INFO_FIELDS_MAX),
    notify: String(notifyCount),
    important: String(importantCount),
    maxImportant: String(INFO_FIELDS_MAX_IMPORTANT),
  });
  const overCap = fields.length > INFO_FIELDS_MAX || importantCount > INFO_FIELDS_MAX_IMPORTANT;
  const reorderHintId = `action-${actionKey}-info-reorder-hint`;

  return (
    <div className="form-group custom-fields info-fields">
      <div className="custom-fields__header">
        <span className="form-label custom-fields__label">{t('settings-actions-info-label')}</span>
        <span className={`form-counter${overCap ? ' form-counter--over' : ''}`} aria-live="polite">
          {counterText}
        </span>
      </div>
      <p className="form-hint">{t('settings-actions-info-help')}</p>
      {/* Information, not a problem: every mark is saved, and the list order picks what agents see. */}
      <div role="status">
        {notifyCount > NOTIFICATION_ROWS && (
          <p className="settings-page__notice info-fields__note">
            {t('settings-actions-info-notify-note', { count: String(notifyCount), max: String(NOTIFICATION_ROWS) })}
          </p>
        )}
      </div>
      <p className="sr-only" id={reorderHintId}>{t('settings-actions-info-reorder-hint')}</p>
      <p className="sr-only" role="status">{announcement}</p>

      {fields.length === 0 && (
        <p className="custom-fields__empty">{t('settings-actions-info-empty')}</p>
      )}

      {fields.map((field, index) => {
        const id = (part: string) => rowId(field.rowKey, part);
        const isMatch = field.display === 'match';
        const isLink = field.display === 'link';
        const isExternalTag = field.display === 'externalTag';
        const labelError = inputError(index, 'label', field.label);
        const attributeKeyError = isLink || isExternalTag ? null : inputError(index, 'attributeKey', field.attributeKey);
        const linkUrlError = isLink ? inputError(index, 'linkUrl', field.linkUrl) : null;
        const wholeAttribute = isLink ? wholeAddressAttribute(field) : null;
        const linkHttp = isLink && /^http:\/\//i.test(field.linkUrl.trim());
        const linkUrlNotes = [
          linkHttp && id('link-http'),
          wholeAttribute !== null && id('link-whole'),
          wholeAttribute !== null && isCustomerSetAttribute(wholeAttribute) && id('link-whole-warning'),
        ].filter(Boolean).join(' ');
        const matchesError = isMatch ? inputError(index, 'matches', '', true) : null;
        const summaryKey = isExternalTag ? '' : isLink ? linkAttributeKey(field) ?? '' : field.attributeKey.trim();
        const unreachable = unreachableConditions(field);
        const rowError = rowServerError(index);
        const isNew = Boolean(field.unsaved);
        const open = isNew || openRow === field.rowKey;
        const incomplete = !open && (rowHasProblem(validation, index) || serverError?.fieldIndex === index);
        const waiting = Boolean(pending) && !isNew;
        const name = field.label.trim() || t('settings-actions-fields-new');
        const atMatchCap = field.matches.length >= INFO_FIELD_MATCHES_MAX;
        const importantLocked = !field.important && importantCount >= INFO_FIELDS_MAX_IMPORTANT;
        const dropClass = drag && drag.over === field.rowKey && drag.from !== field.rowKey
          ? drag.after ? ' info-field--drop-after' : ' info-field--drop-before'
          : '';

        return (
          <div
            id={id('row')}
            className={`custom-field info-field${open ? ' custom-field--open' : ''}${drag?.from === field.rowKey ? ' info-field--dragging' : ''}${dropClass}`}
            key={field.rowKey}
            onDragOver={(e) => onDragOver(e, field.rowKey)}
            onDrop={onDrop}
          >
            <div className="custom-field__toolbar" onKeyDown={(e) => handleRowKeyDown(e, index)}>
              {!isNew && (
                <button
                  type="button"
                  id={id('handle')}
                  className="info-field__handle"
                  draggable={!pending && fields.length > 1}
                  data-info-focus="handle"
                  aria-label={t('settings-actions-info-reorder', { name })}
                  aria-describedby={reorderHintId}
                  title={t('settings-actions-info-reorder-title')}
                  disabled={waiting || fields.length < 2}
                  onDragStart={(e) => onDragStart(e, field.rowKey)}
                  onDragEnd={() => setDrag(null)}
                >
                  <GripIcon />
                </button>
              )}
              <button
                type="button"
                id={id('summary')}
                className="custom-field__summary"
                aria-expanded={open}
                aria-controls={id('body')}
                aria-describedby={isNew ? undefined : reorderHintId}
                data-info-focus="summary"
                disabled={isNew || waiting}
                title={waiting ? t('settings-actions-fields-finish-first') : undefined}
                onClick={() => setOpenRow(open ? null : field.rowKey)}
              >
                <svg
                  className={`custom-field__chevron${open ? ' custom-field__chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <span className="custom-field__summary-text">
                  <span
                    className={`info-field__summary-label${field.label.trim() ? '' : ' custom-field__summary-key--new'}`}
                  >
                    <bdi>{name}</bdi>
                  </span>
                  {summaryKey && (
                    <span className="custom-field__summary-key info-field__summary-key"><bdi>{summaryKey}</bdi></span>
                  )}
                  <span className="custom-field__summary-meta">
                    {isMatch
                      ? t(field.matches.length === 1 ? 'settings-actions-info-summary-match-one' : 'settings-actions-info-summary-match-many', {
                        count: String(field.matches.length),
                      })
                      : t(isExternalTag
                        ? 'settings-actions-info-summary-external-tag'
                        : isLink ? 'settings-actions-info-summary-link' : 'settings-actions-info-summary-value')}
                  </span>
                </span>
                <span className="info-field__badges">
                  {field.important && (
                    <span className="info-field__badge info-field__badge--important">{t('settings-actions-info-badge-important')}</span>
                  )}
                  {field.notify && (
                    <span className="info-field__badge info-field__badge--notify">{t('settings-actions-info-badge-notify')}</span>
                  )}
                  {field.channels !== 'all' && (
                    <span className="info-field__badge info-field__badge--channel">
                      {t(field.channels === 'voice' ? 'settings-actions-info-badge-voice' : 'settings-actions-info-badge-digital')}
                    </span>
                  )}
                  {incomplete && (
                    <span className="custom-field__badge">{t('settings-actions-fields-incomplete')}</span>
                  )}
                </span>
              </button>
              {!isNew && (
              <div className="custom-field__controls">
                <button
                  type="button"
                  id={id('up')}
                  className="custom-field__icon-btn"
                  data-info-focus="up"
                  aria-label={t('settings-actions-fields-move-up')}
                  title={t('settings-actions-fields-move-up')}
                  disabled={index === 0 || waiting}
                  onClick={() => moveTo(index, index - 1, 'up')}
                >
                  <ChevronIcon up />
                </button>
                <button
                  type="button"
                  id={id('down')}
                  className="custom-field__icon-btn"
                  data-info-focus="down"
                  aria-label={t('settings-actions-fields-move-down')}
                  title={t('settings-actions-fields-move-down')}
                  disabled={index === fields.length - 1 || waiting}
                  onClick={() => moveTo(index, index + 1, 'down')}
                >
                  <ChevronIcon up={false} />
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  disabled={waiting}
                  onClick={() => remove(index)}
                >
                  {t('settings-actions-fields-remove')}
                </button>
              </div>
              )}
            </div>

            {open && (
            <div id={id('body')} className="custom-field__body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor={id('label')}>
                  {t('settings-actions-fields-label-label')}
                </label>
                <input
                  id={id('label')}
                  dir={fieldDir(field.label, t('settings-actions-info-label-placeholder'))}
                  className={`form-input${labelError ? ' form-input--invalid' : ''}`}
                  type="text"
                  maxLength={INFO_FIELD_LABEL_MAX_LENGTH}
                  value={field.label}
                  placeholder={t('settings-actions-info-label-placeholder')}
                  aria-invalid={Boolean(labelError)}
                  onChange={(e) => update(index, { label: e.target.value })}
                  onBlur={(e) => markTouched(field.rowKey, 'label', e.relatedTarget)}
                />
                <div className="form-hint-row">
                  {labelError
                    ? <p className="form-error" role="alert">{labelError}</p>
                    : <p className="form-hint">{t('settings-actions-info-label-help')}</p>}
                  <span className="form-counter" aria-live="polite">
                    {field.label.length}/{INFO_FIELD_LABEL_MAX_LENGTH}
                  </span>
                </div>
              </div>
              {!isLink && !isExternalTag && (
              <div className="form-group">
                <label className="form-label" htmlFor={id('attribute-key')}>
                  {t('settings-actions-fields-attribute-key-label')}
                </label>
                <input
                  id={id('attribute-key')}
                  dir={fieldDir(field.attributeKey, t('settings-actions-fields-attribute-key-placeholder'))}
                  className={`form-input info-field__attribute-input${attributeKeyError ? ' form-input--invalid' : ''}`}
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH}
                  value={field.attributeKey}
                  placeholder={t('settings-actions-fields-attribute-key-placeholder')}
                  aria-invalid={Boolean(attributeKeyError)}
                  aria-describedby={isCustomerSetAttribute(field.attributeKey) ? id('attribute-warning') : undefined}
                  onChange={(e) => update(index, { attributeKey: e.target.value })}
                  onBlur={(e) => markTouched(field.rowKey, 'attributeKey', e.relatedTarget)}
                />
                <div className="form-hint-row">
                  {attributeKeyError
                    ? <p className="form-error" role="alert">{attributeKeyError}</p>
                    : <p className="form-hint">{t('settings-actions-info-attribute-key-help')}</p>}
                  <span className="form-counter" aria-live="polite">
                    {field.attributeKey.length}/{INFO_FIELD_ATTRIBUTE_KEY_MAX_LENGTH}
                  </span>
                </div>
                {isCustomerSetAttribute(field.attributeKey) && (
                  <p className="form-hint info-field__warning" id={id('attribute-warning')}>
                    {t('settings-actions-info-context-warning')}
                  </p>
                )}
              </div>
              )}
            </div>

            <fieldset className="info-field__display">
              <legend className="form-label">{t('settings-actions-info-display-label')}</legend>
              <div className="info-field__choices">
                {DISPLAYS.map((display) => (
                  <label className="settings-page__toggle info-field__choice" key={display}>
                    <input
                      type="radio"
                      name={id('display')}
                      value={display}
                      checked={field.display === display}
                      onChange={() => update(index, { display })}
                    />
                    <span>{t(DISPLAY_LABEL_KEYS[display])}</span>
                  </label>
                ))}
              </div>
              <p className="form-hint">{t(DISPLAY_HELP_KEYS[field.display])}</p>
            </fieldset>

            {isLink && (
              <div className="form-group">
                <label className="form-label" htmlFor={id('link-url')}>
                  {t('settings-actions-info-link-url-label')}
                </label>
                <input
                  id={id('link-url')}
                  dir="ltr"
                  className={`form-input info-field__link-input${linkUrlError ? ' form-input--invalid' : ''}`}
                  type="text"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={INFO_LINK_URL_MAX_LENGTH}
                  value={field.linkUrl}
                  placeholder={t('settings-actions-info-link-url-placeholder')}
                  aria-invalid={Boolean(linkUrlError)}
                  aria-describedby={linkUrlNotes || undefined}
                  onChange={(e) => update(index, { linkUrl: e.target.value })}
                  onBlur={(e) => markTouched(field.rowKey, 'linkUrl', e.relatedTarget)}
                />
                <div className="form-hint-row">
                  {linkUrlError
                    ? <p className="form-error" role="alert">{linkUrlError}</p>
                    : <p className="form-hint">{t('settings-actions-info-link-url-help')}</p>}
                  <span className="form-counter" aria-live="polite">
                    {field.linkUrl.length}/{INFO_LINK_URL_MAX_LENGTH}
                  </span>
                </div>
                {linkHttp && (
                  <p className="form-hint info-field__warning" id={id('link-http')}>
                    {t('settings-actions-info-link-http-hint')}
                  </p>
                )}
                {wholeAttribute !== null && (
                  <p className="form-hint" id={id('link-whole')}>
                    {t('settings-actions-info-link-whole-hint')}
                  </p>
                )}
                {wholeAttribute !== null && isCustomerSetAttribute(wholeAttribute) && (
                  <p className="form-hint info-field__warning" id={id('link-whole-warning')}>
                    {t('settings-actions-info-context-warning')}
                  </p>
                )}
              </div>
            )}

            {isMatch && (
              <fieldset className="custom-field__options">
                <legend className="form-label custom-field__options-legend">
                  {t('settings-actions-info-matches-label')}
                </legend>
                <span className="form-counter custom-field__options-counter" aria-live="polite">
                  {t('settings-actions-fields-options-counter', {
                    n: String(field.matches.length),
                    max: String(INFO_FIELD_MATCHES_MAX),
                  })}
                </span>
                <p className="form-hint custom-field__options-help">{t('settings-actions-info-matches-help')}</p>
                {field.matches.map((match, matchIndex) => {
                  const matchId = (input: string) => `${id('match')}-${match.rowKey}-${input}`;
                  const equalsError = match.op === 'any' ? null : matchError(index, matchIndex, 'equals', match.equals);
                  const textError = matchError(index, matchIndex, 'text', match.text);
                  const removeLabel = t('settings-actions-info-match-remove-named', { n: String(matchIndex + 1) });
                  const numeric = isNumberOp(match.op);
                  const valueMax = numeric ? INFO_MATCH_NUMBER_MAX_LENGTH : INFO_MATCH_EQUALS_MAX_LENGTH;
                  const updateMatch = (patch: Partial<typeof match>) => update(index, {
                    matches: field.matches.map((m, i) => (i === matchIndex ? { ...m, ...patch } : m)),
                  });
                  return (
                    <div className="custom-field__option" key={match.rowKey}>
                      <div className="info-field__condition">
                      <div className="form-row">
                        <div className="form-group info-field__op">
                          <label className="form-label" htmlFor={matchId('op')}>
                            {t('settings-actions-info-match-op')}
                          </label>
                          <select
                            id={matchId('op')}
                            className="form-select"
                            value={match.op}
                            aria-describedby={matchId('op-hint')}
                            onChange={(e) => updateMatch({ op: e.target.value as InfoMatchOp })}
                          >
                            {INFO_MATCH_OPS.map((op) => (
                              <option key={op} value={op}>{t(OP_LABEL_KEYS[op])}</option>
                            ))}
                          </select>
                        </div>
                        {match.op !== 'any' && (
                        <div className="form-group">
                          <label className="form-label" htmlFor={matchId('equals')}>
                            {t('settings-actions-info-match-value')}
                          </label>
                          <input
                            id={matchId('equals')}
                            dir={numeric ? 'ltr' : fieldDir(match.equals)}
                            className={`form-input${equalsError ? ' form-input--invalid' : ''}`}
                            type="text"
                            inputMode={numeric ? 'decimal' : undefined}
                            maxLength={valueMax}
                            value={match.equals}
                            aria-invalid={Boolean(equalsError)}
                            onChange={(e) => updateMatch({ equals: e.target.value })}
                            onBlur={(e) => markTouched(match.rowKey, 'equals', e.relatedTarget)}
                          />
                          <div className="form-hint-row">
                            {equalsError
                              ? <p className="form-error" role="alert">{equalsError}</p>
                              : numeric ? <p className="form-hint">{t('settings-actions-info-match-number-help')}</p> : <span />}
                            <span className={`form-counter${match.equals.length > valueMax ? ' form-counter--over' : ''}`} aria-live="polite">
                              {match.equals.length}/{valueMax}
                            </span>
                          </div>
                        </div>
                        )}
                        <div className="form-group">
                          <label className="form-label" htmlFor={matchId('text')}>
                            {t('settings-actions-info-match-text')}
                          </label>
                          <input
                            id={matchId('text')}
                            dir={fieldDir(match.text)}
                            className={`form-input${textError ? ' form-input--invalid' : ''}`}
                            type="text"
                            maxLength={INFO_MATCH_TEXT_MAX_LENGTH}
                            value={match.text}
                            aria-invalid={Boolean(textError)}
                            onChange={(e) => updateMatch({ text: e.target.value })}
                            onBlur={(e) => markTouched(match.rowKey, 'text', e.relatedTarget)}
                          />
                          <div className="form-hint-row">
                            {textError ? <p className="form-error" role="alert">{textError}</p> : <span />}
                            <span className={`form-counter${match.text.length > INFO_MATCH_TEXT_MAX_LENGTH ? ' form-counter--over' : ''}`} aria-live="polite">
                              {match.text.length}/{INFO_MATCH_TEXT_MAX_LENGTH}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="form-hint info-field__op-hint" id={matchId('op-hint')}>
                        {t(OP_HINT_KEYS[match.op])}
                      </p>
                      {unreachable.has(match.rowKey) && (
                        <p className="form-hint info-field__warning">{t('settings-actions-info-match-unreachable')}</p>
                      )}
                      </div>
                      <button
                        type="button"
                        className="btn btn--secondary btn--small"
                        aria-label={removeLabel}
                        title={removeLabel}
                        onClick={() => update(index, { matches: field.matches.filter((_, i) => i !== matchIndex) })}
                        disabled={field.matches.length <= 1}
                      >
                        {t('settings-actions-info-match-remove')}
                      </button>
                    </div>
                  );
                })}
                <div className="action-card__actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      if (atMatchCap) return;
                      update(index, { matches: [...field.matches, emptyInfoMatch()] });
                    }}
                    disabled={atMatchCap}
                  >
                    {t('settings-actions-info-match-add')}
                  </button>
                  {atMatchCap && (
                    <span className="form-hint action-card__actions-hint">
                      {t('settings-actions-info-match-add-cap')}
                    </span>
                  )}
                </div>
                {matchesError && <p className="form-error" role="alert">{matchesError}</p>}
              </fieldset>
            )}

            <div className="form-row">
              <div className="form-group info-field__channels">
                <label className="form-label" htmlFor={id('channels')}>
                  {t('settings-actions-info-channels-label')}
                </label>
                <select
                  id={id('channels')}
                  className="form-select"
                  value={field.channels}
                  onChange={(e) => update(index, { channels: e.target.value as InfoFieldChannels })}
                >
                  {CHANNELS.map((channel) => (
                    <option key={channel} value={channel}>{t(CHANNEL_LABEL_KEYS[channel])}</option>
                  ))}
                </select>
                <p className="form-hint">{t('settings-actions-info-channels-help')}</p>
              </div>
            </div>

            <div className="form-group custom-field__required">
              <label className="settings-page__toggle">
                <input
                  type="checkbox"
                  checked={field.notify}
                  aria-describedby={id('notify-help')}
                  onChange={(e) => update(index, { notify: e.target.checked })}
                />
                <span>{t('settings-actions-info-notify')}</span>
              </label>
              <p className="form-hint" id={id('notify-help')}>
                {t('settings-actions-info-notify-help', { max: String(NOTIFICATION_ROWS) })}
              </p>
            </div>

            <div className="form-group custom-field__required">
              <label className="settings-page__toggle">
                <input
                  type="checkbox"
                  checked={field.important}
                  disabled={importantLocked}
                  aria-describedby={id('important-help')}
                  onChange={(e) => update(index, { important: e.target.checked })}
                />
                <span>{t('settings-actions-info-important')}</span>
              </label>
              <p className="form-hint" id={id('important-help')}>
                {t(importantLocked ? 'settings-actions-info-important-cap-reached' : 'settings-actions-info-important-help', {
                  max: String(INFO_FIELDS_MAX_IMPORTANT),
                })}
              </p>
            </div>

            {rowError && (
              <p className="form-error" role="alert">{rowError}</p>
            )}
            {isNew && (
              <div className="custom-field__new-actions">
                <button type="button" className="btn btn--secondary btn--small" {...discardButtonProps} onClick={() => void cancelField(index)}>
                  {t('settings-actions-fields-cancel')}
                </button>
                <button type="button" className="btn btn--primary btn--small" onClick={() => saveField(index)}>
                  {t('settings-actions-fields-save')}
                </button>
              </div>
            )}
            </div>
            )}
          </div>
        );
      })}

      {!pending && (
      <div className="action-card__actions">
        <button
          ref={addRef}
          type="button"
          className="btn btn--primary action-strong-btn"
          onClick={add}
          disabled={atTotalCap}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('settings-actions-fields-add')}
        </button>
        {atTotalCap && (
          <span className="form-hint action-card__actions-hint">
            {t('settings-actions-info-add-cap')}
          </span>
        )}
      </div>
      )}

      {blockError && (
        <p className="form-error" role="alert">{blockError}</p>
      )}
    </div>
  );
}
